import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { motion, AnimatePresence } from "framer-motion";
import HydrotecBrandSection from "../components/HydrotecBrandSection";
import {
  ChevronRight,
  ArrowUpRight,
  Droplets,
  Activity,
  Factory,
  ShieldCheck,
  Gauge,
} from "lucide-react";
import { assets } from "../data/siteData";

const heroStats = [
  { value: "550+", label: "Installations & impact points" },
  { value: "24/7", label: "Connected monitoring ready" },
  { value: "IoT", label: "Smart IoT infrastructure" },
];

const recognitionLogos = [
  {
    logo: assets.gwp,
    link: "https://www.gwp.org/en/GWP-Mediterranean/WE-ACT/News-List-Page/20232/30-finalists-water-changemaker-awards-2023/",
  },
  {
    logo: assets.ciscoa,
    link: "https://blogs.cisco.com/csr/meet-hydrotec-solutions-an-india-impact-runner-up-of-the-cisco-global-problem-solver-challenge-2020dtidpblgblg001473oidpstoth022646",
  },
  { logo: assets.gist, link: "https://venturewell.org/blog/world-water-day/" },
  {
    logo: assets.bayer,
    link: "https://getinthering.co/discover-bayer-social-innovation-award-winners/",
  },
  {
    logo: assets.bricsa,
    link: "https://bricswomen.com/2025/07/06/brics-women-led-startup-contest-and-solutions-awards-announce-2025-winners/",
  },
  {
    logo: assets.wtia,
    link: "https://www.niti.gov.in/sites/default/files/2020-03/Coffee_Table_Book.pdf",
  },
  {
    logo: assets.una,
    link: "https://2021-2025.state.gov/un-ocean-conference-2022/",
  },
  {
    logo: assets.singaporea,
    link: "https://www.linkedin.com/posts/siww_%F0%9D%90%80%F0%9D%90%A5%F0%9D%90%A5-%F0%9D%90%A0%F0%9D%90%9E%F0%9D%90%9A%F0%9D%90%AB%F0%9D%90%9E%F0%9D%90%9D-%F0%9D%90%AE%F0%9D%90%A9-%F0%9D%90%9A%F0%9D%90%A7%F0%9D%90%9D-%F0%9D%90%A5%F0%9D%90%A8%F0%9D%90%A8-activity-7208499893395476480-3NP5/",
  },
  {
    logo: assets.slt,
    link: "https://yourstory.com/herstory/2020/04/startup-iot-enabled-drinking-water-atm-hydrotec",
  },
  {
    logo: assets.leadersa,
    link: "https://www.facebook.com/watch/?v=1218474175296289",
  },
  {
    logo: assets.ftifc,
    link: "https://www.facebook.com/IFCwbg/photos/access-to-safe-drinking-water-is-often-a-challenge-in-rural-india-2020-ftifcawar/10158202294930783/",
  },
  {
    logo: assets.qualcom,
    link: "https://yourstory.com/2021/08/qualcomm-women-entrepreneur-india-finalists",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const stagger = {
  show: {
    transition: { staggerChildren: 0.12 },
  },
};

const awardCards = [
  {
    title: "UN Ocean Conference Recognition",
    image: assets.una || assets.heroWaterPlant,
    to: "https://2021-2025.state.gov/un-ocean-conference-2022/",
  },
  {
    title: "Women Transforming India",
    image: assets.wtia || assets.dashboardPreview,
    to: "/about",
  },
  {
    title: "Singapore Water Prize Honour",
    image: assets.singaporea || assets.waterAtmCity,
    to: "/about",
  },
  {
    title: "BRICS",
    image: assets.bricsa || assets.engineer,
    to: "https://bricswomen.com/2025/07/06/brics-women-led-startup-contest-and-solutions-awards-announce-2025-winners/",
  },
  {
    title: "Cisco Global Problem Solver",
    image: assets.ciscoa || assets.waterr || assets.waterAtmCity,
    to: "https://blogs.cisco.com/csr/meet-hydrotec-solutions-an-india-impact-runner-up-of-the-cisco-global-problem-solver-challenge-2020dtidpblgblg001473oidpstoth022646",
  },
  {
    title: "She Loves Tech",
    image: assets.slt || assets.waterr || assets.waterAtmCity,
    to: "https://yourstory.com/herstory/2020/04/startup-iot-enabled-drinking-water-atm-hydrotec",
  },
  {
    title: "Bayer Water Award",
    image: assets.bayer || assets.waterr || assets.waterAtmCity,
    to: "/about",
  },
  {
    title: "GIST USA",
    image: assets.gist || assets.waterr || assets.waterAtmCity,
    to: "https://x.com/GISTNetwork/status/1541915242095075331",
  },
  {
    title: "Leader of Tomorrow",
    image: assets.leadersa || assets.chlorinationPlant,
    to: "https://bestmediainfo.com/2022/12/et-now-felicitates-winners-of-10th-season-of-leaders-of-tomorrow-awards",
  },
];

const solutionCards = [
  {
    title: "Water ATMs",
    text: "Smart public access kiosks for communities, campuses, stations, cities, and high-footfall locations.",
    icon: Droplets,
    to: "https://www.arosia.in",
    external: true,
  },
  {
    title: "Water Treatment",
    text: "Integrated purification using filtration, RO, UV, chlorination, and system-grade process controls.",
    icon: ShieldCheck,
    to: "/solutions/water-treatment",
  },
  {
    title: "Pump Systems",
    text: "Flow, pressure, transfer, dosing support, and infrastructure reliability for real operating conditions.",
    icon: Gauge,
    to: "/solutions/pumps",
  },
  {
    title: "Online Monitoring",
    text: "Real-time visibility into water quality, dispensing, usage, alerts, service, and performance.",
    icon: Activity,
    to: "/solutions/online-monitoring",
  },
];

const featuredSections = [
  {
    eyebrow: "Water ATM Solutions",
    title: "Public water access, reimagined for modern places.",
    text: "Hydrotec designs refill water infrastructure that feels dependable, visible, and easy to use in real public environments.",
    image: assets.heroWaterPlant || assets.waterAtmCity,
    to: "/solutions/water-atm",
  },
];

const shapingCards = [
  {
    title: "Sustainability and ESG Solutions",
    image: assets.sustainability || assets.waterAtmCity,
    text: "Public-facing systems that make safe drinking water more accessible in cities, campuses, and high-footfall locations.",
    to: "/solutions/water-atm",
  },
  {
    title: "Earth and Environment",
    image: assets.earth || assets.chlorinationPlant,
    text: "Engineered purification, chlorination, and process systems for dependable output and long-term field operation.",
    to: "/solutions/water-treatment",
  },
  {
    title: "Water",
    image: assets.waterr || assets.waterAtmCity,
    text: "Digital oversight for water quality, usage, alerts, service events, and infrastructure performance.",
    to: "/monitoring",
  },
  {
    title: "Connected Monitoring",
    image: assets.dashboardPreview,
    text: "Connected dashboards help teams monitor water quality, status, dispensing, alerts, and performance across installations.",
    to: "/monitoring",
  },
];

const showcaseProjects = [
  {
    title: "Arosia Water ATMs",
    text: "Across cities, communities, and public spaces, we build dependable water infrastructure that expands access, improves resilience, and supports long-term public impact.",
    image: assets.waterAtmCity || assets.heroWaterPlant,
    to: "https://arosia.in/",
    cta: "Explore Arosia",
    external: true,
  },
  {
    title: "Connected Monitoring Systems",
    text: "We bring water quality, dispensing, alerts, and asset performance into one digital operating layer for teams that need visibility across sites.",
    image: assets.dashboardPreview || assets.waterr,
    to: "/monitoring",
    cta: "View Monitoring",
  },
  {
    title: "Treatment Purification Infrastructure",
    text: "From purification and chlorination to integrated field systems, we engineer water treatment solutions for real-world operating conditions.",
    image: assets.chlorinationPlant || assets.engineer,
    to: "/solutions/water-treatment",
    cta: "View Treatment",
  },
  {
    title: "Pioneering Water Projects",
    text: "We work with partners to challenge outdated water delivery models and create practical, scalable systems for sustainable environments.",
    image: assets.water || assets.heroWaterPlant,
    to: "/projects",
    cta: "View Projects",
  },
];

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function PrimaryButton({ to, children, light = false, icon = null }) {
  return (
    <Link
      to={to}
      className={cx(
        "inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold transition duration-300",
        light
          ? "border border-cyan-300/25 bg-cyan-400/10 text-white hover:bg-cyan-400/16"
          : "bg-cyan-400 text-slate-950 hover:bg-cyan-300"
      )}
    >
      {children}
      {icon}
    </Link>
  );
}

function SmartLink({ to, children, className }) {
  const isExternal = /^https?:\/\//.test(to);
  if (isExternal) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

function SectionEyebrow({ children, dark = false }) {
  return (
    <p
      className={cx(
        "mb-4 text-[11px] font-semibold uppercase tracking-[0.34em]",
        dark ? "text-cyan-700" : "text-cyan-300/80"
      )}
    >
      {children}
    </p>
  );
}

function FeatureCard({ item, index }) {
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55, ease: "easeOut", delay: index * 0.06 }}
      className="group rounded-[2rem] border border-cyan-300/15 bg-white/10 p-6 shadow-[0_20px_60px_rgba(0,20,40,0.28)] backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:bg-white/14"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-400/10 ring-1 ring-cyan-300/20">
        <Icon className="h-6 w-6 text-cyan-300" />
      </div>

      <h3 className="mt-5 text-2xl font-semibold tracking-tight text-white">
        {item.title}
      </h3>

      <p className="mt-3 leading-7 text-white/80">{item.text}</p>

      <SmartLink
        to={item.to}
        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 transition group-hover:text-cyan-200"
      >
        Explore <ArrowUpRight className="h-4 w-4" />
      </SmartLink>
    </motion.div>
  );
}

function EditorialSection({ eyebrow, title, text, image, to, reverse = false }) {
  return (
    <section className="bg-[#071520] text-white">
      <div className="mx-auto grid max-w-[1600px] items-stretch lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, x: reverse ? 30 : -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className={cx(
            "relative min-h-[560px] overflow-hidden",
            reverse ? "lg:order-2" : ""
          )}
        >
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition duration-[1800ms] hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#021018]/55 via-transparent to-transparent" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: reverse ? -30 : 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className={cx(
            "flex items-center bg-gradient-to-br from-[#081722] via-[#0a2130] to-[#0d2a3c]",
            reverse ? "lg:order-1" : ""
          )}
        >
          <div className="w-full max-w-2xl px-6 py-20 sm:px-10 lg:px-16 xl:px-20">
            <SectionEyebrow>{eyebrow}</SectionEyebrow>
            <h2 className="max-w-xl text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl lg:leading-[1.02]">
              {title}
            </h2>
            <p className="mt-6 max-w-xl text-lg leading-8 text-white/72">
              {text}
            </p>
            <div className="mt-10">
              <PrimaryButton to={to} light>
                Learn More <ChevronRight className="ml-2 h-4 w-4" />
              </PrimaryButton>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const [awardIndex, setAwardIndex] = useState(0);
  const [awardStep, setAwardStep] = useState(0);
  const [awardVisibleCards, setAwardVisibleCards] = useState(3);

  const [sectorIndex, setSectorIndex] = useState(0);
  const [sectorStep, setSectorStep] = useState(0);
  const [visibleCards, setVisibleCards] = useState(3);

  const awardTrackRef = useRef(null);
  const awardIntervalRef = useRef(null);
  const sectorTrackRef = useRef(null);

  const [showcaseIndex, setShowcaseIndex] = useState(0);

  const megaContent = useMemo(
    () => ({
      solutions: {
        title: "Solutions",
        links: [
          {
            name: "Water ATMs",
            desc: "Smart public water access systems",
            to: "/solutions/water-atm",
          },
          {
            name: "Water Treatment",
            desc: "Filtration, RO, UV, chlorination",
            to: "/solutions/water-treatment",
          },
          {
            name: "Pump Systems",
            desc: "Pressure, transfer, dosing support",
            to: "/solutions/pumps",
          },
          {
            name: "Online Monitoring",
            desc: "Connected dashboards and alerts",
            to: "/solutions/online-monitoring",
          },
        ],
      },
      sectors: {
        title: "Sectors",
        links: [
          {
            name: "Cities & Municipalities",
            desc: "Infrastructure-ready public water systems",
            to: "/projects",
          },
          {
            name: "Institutions",
            desc: "Reliable water systems for campuses and facilities",
            to: "/projects",
          },
          {
            name: "Communities",
            desc: "Safe water access for rural and peri-urban deployment",
            to: "/projects",
          },
          {
            name: "Industrial Sites",
            desc: "Monitoring-led water and wastewater readiness",
            to: "/projects",
          },
        ],
      },
    }),
    []
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setShowcaseIndex((prev) =>
        prev === showcaseProjects.length - 1 ? 0 : prev + 1
      );
    }, 4500);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const updateAwardLayout = () => {
      const width = window.innerWidth;

      let visible = 3;
      if (width < 768) visible = 1;
      else if (width < 1280) visible = 2;

      setAwardVisibleCards(visible);

      const track = awardTrackRef.current;
      if (!track) return;

      const firstCard = track.querySelector("[data-award-card]");
      if (!firstCard) return;

      const styles = window.getComputedStyle(firstCard);
      const marginRight = parseFloat(styles.marginRight || "0");
      const cardWidth = firstCard.getBoundingClientRect().width;

      setAwardStep(cardWidth + marginRight);
    };

    updateAwardLayout();
    window.addEventListener("resize", updateAwardLayout);

    return () => window.removeEventListener("resize", updateAwardLayout);
  }, []);

  useEffect(() => {
    const updateSectorLayout = () => {
      const width = window.innerWidth;

      let visible = 3;
      if (width < 768) visible = 1;
      else if (width < 1280) visible = 2;

      setVisibleCards(visible);

      const track = sectorTrackRef.current;
      if (!track) return;

      const firstCard = track.querySelector("[data-sector-card]");
      if (!firstCard) return;

      const styles = window.getComputedStyle(firstCard);
      const marginRight = parseFloat(styles.marginRight || "0");
      const cardWidth = firstCard.getBoundingClientRect().width;

      setSectorStep(cardWidth + marginRight);
    };

    updateSectorLayout();
    window.addEventListener("resize", updateSectorLayout);

    return () => window.removeEventListener("resize", updateSectorLayout);
  }, []);

  const maxAwardIndex = Math.max(awardCards.length - awardVisibleCards, 0);
  const maxSectorIndex = Math.max(shapingCards.length - visibleCards, 0);

  useEffect(() => {
    if (awardIndex > maxAwardIndex) setAwardIndex(0);
  }, [awardIndex, maxAwardIndex]);

  useEffect(() => {
    if (sectorIndex > maxSectorIndex) setSectorIndex(0);
  }, [sectorIndex, maxSectorIndex]);

  useEffect(() => {
    awardIntervalRef.current = setInterval(() => {
      setAwardIndex((prev) => (prev >= maxAwardIndex ? 0 : prev + 1));
    }, 3500);

    return () => {
      if (awardIntervalRef.current) clearInterval(awardIntervalRef.current);
    };
  }, [maxAwardIndex]);

  const handlePrevAward = () => {
    setAwardIndex((prev) => (prev <= 0 ? maxAwardIndex : prev - 1));
  };

  const handleNextAward = () => {
    setAwardIndex((prev) => (prev >= maxAwardIndex ? 0 : prev + 1));
  };

  const handlePrevSector = () => {
    setSectorIndex((prev) => (prev <= 0 ? maxSectorIndex : prev - 1));
  };

  const handleNextSector = () => {
    setSectorIndex((prev) => (prev >= maxSectorIndex ? 0 : prev + 1));
  };

  const handlePrevShowcase = () => {
    setShowcaseIndex((prev) =>
      prev === 0 ? showcaseProjects.length - 1 : prev - 1
    );
  };

  const handleNextShowcase = () => {
    setShowcaseIndex((prev) =>
      prev === showcaseProjects.length - 1 ? 0 : prev + 1
    );
  };

  const whatWeDoBackground =
    assets.watertech || assets.chlorinationPlant || assets.waterAtmCity;

  const currentShowcase = showcaseProjects[showcaseIndex];
  const showcaseIsExternal = /^https?:\/\//.test(currentShowcase.to);

  return (
    <div className="bg-[#eef8fb] text-white">
      <SEO
        title="Water Treatment Company in Kolkata, Newtown, Rajarhat | Water ATMs, Pumps & Purification — West Bengal"
        description="Hydrotec Solutions is a water treatment company in Kolkata providing water ATMs, water purification systems, pump solutions, bacteria removal, UV disinfection, and IoT monitoring for Newtown, Rajarhat, and all of West Bengal."
        keywords="water treatment company Kolkata, water ATM Kolkata, water purification West Bengal, water infrastructure Newtown, water solutions Rajarhat, pump systems Kolkata, bacteria removal West Bengal, water monitoring Kolkata"
        canonical="https://hydroteclimited.com/"
      />
      <section className="relative min-h-screen overflow-hidden bg-[#06131d]">
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1.02 }}
          transition={{ duration: 6, ease: "easeOut" }}
        >
          <img
            src={assets.sustainability || assets.waterAtmForest}
            alt="Hydrotec water infrastructure"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#031019]/78 via-[#072131]/52 to-[#0a3145]/24" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#041119]/58 via-transparent to-[#03131d]/35" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.18),transparent_22%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.15),transparent_28%)]" />
        </motion.div>

        <div className="relative z-10 mx-auto flex min-h-screen max-w-[1600px] flex-col justify-between px-4 pb-10 pt-44 sm:px-6 md:pt-48 lg:px-8 lg:pt-52">
          <div className="grid items-end gap-10 lg:grid-cols-[1.15fr_0.85fr]">
            <motion.div
              initial={{ opacity: 0, y: 36 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
              className="max-w-4xl"
            >
              <SectionEyebrow>Hydrotec Solutions</SectionEyebrow>

              <h1 className="max-w-5xl text-[2.8rem] font-semibold tracking-[-0.04em] sm:text-6xl lg:text-[4rem] lg:leading-[0.94]">
                Water Treatment for Sustainable Environments
              </h1>

              <p className="mt-7 max-w-2xl text-lg leading-8 text-white/78 sm:text-xl">
                Smart water ATMs, water treatment systems, pump infrastructure,
                connected monitoring, chlorination, and wastewater-ready
                solutions for modern cities, communities, buildings, campuses,
                and critical utility sites.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <PrimaryButton to="/solutions">
                  Explore Solutions <ChevronRight className="ml-2 h-4 w-4" />
                </PrimaryButton>
                <PrimaryButton to="/projects" light>
                  View Projects <ArrowUpRight className="ml-2 h-4 w-4" />
                </PrimaryButton>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: "easeOut", delay: 0.22 }}
              className="hidden lg:flex lg:justify-end"
            >
              <div className="w-full max-w-[420px] rounded-[2.2rem] border border-cyan-300/12 bg-white/[0.08] p-6 shadow-[0_20px_70px_rgba(0,20,50,0.26)] backdrop-blur-xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-cyan-200/56">
                  Connected infrastructure
                </p>

                <div className="mt-6 space-y-5">
                  <div className="rounded-[1.6rem] border border-cyan-300/10 bg-[#04131c]/35 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm uppercase tracking-[0.24em] text-white/48">
                          Monitoring
                        </p>
                        <p className="mt-3 text-3xl font-semibold text-white">
                          24/7 Visibility
                        </p>
                      </div>
                      <Activity className="h-6 w-6 text-cyan-300" />
                    </div>
                    <p className="mt-4 text-sm leading-7 text-white/68">
                      Water quality, system health, alerts, dispensing, and site
                      performance in one connected view.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-[1.4rem] border border-cyan-300/10 bg-[#04131c]/35 p-5">
                      <Droplets className="h-5 w-5 text-cyan-300" />
                      <p className="mt-4 text-2xl font-semibold">Safe Water</p>
                      <p className="mt-2 text-sm leading-6 text-white/62">
                        Public access systems
                      </p>
                    </div>

                    <div className="rounded-[1.4rem] border border-cyan-300/10 bg-[#04131c]/35 p-5">
                      <Factory className="h-5 w-5 text-cyan-300" />
                      <p className="mt-4 text-2xl font-semibold">Utilities</p>
                      <p className="mt-2 text-sm leading-6 text-white/62">
                        Treatment and wastewater
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            className="mt-14 grid gap-4 sm:grid-cols-3 lg:mt-20"
          >
            {heroStats.map((item) => (
              <div
                key={item.label}
                className="rounded-[1.8rem] border border-cyan-300/12 bg-white/10 p-6 backdrop-blur-sm"
              >
                <p className="text-3xl font-semibold tracking-tight text-cyan-200 sm:text-4xl">
                  {item.value}
                </p>
                <p className="mt-2 text-sm uppercase tracking-[0.24em] text-white/62">
                  {item.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="bg-[#f3f3f1] text-black">
        <div className="mx-auto max-w-[1480px] px-5 py-14 sm:px-6 lg:px-8 xl:px-10">
          <div className="mb-8 flex items-start justify-between gap-6">
            <div>
              <h2 className="text-[2.45rem] font-semibold leading-[0.96] tracking-[-0.055em] text-black sm:text-[3.25rem] lg:text-[3rem]">
                Shaping the future
              </h2>
            </div>

            <div className="hidden items-center gap-2 lg:flex">
              <button
                type="button"
                onClick={handlePrevSector}
                className="flex h-[50px] w-[50px] items-center justify-center border border-[#d9d9d4] bg-transparent text-black transition hover:bg-white"
              >
                <ChevronRight className="h-4 w-4 rotate-180" />
              </button>
              <button
                type="button"
                onClick={handleNextSector}
                className="flex h-[50px] w-[50px] items-center justify-center border border-[#d9d9d4] bg-transparent text-black transition hover:bg-white"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="overflow-hidden">
            <motion.div
              ref={sectorTrackRef}
              animate={{ x: -(sectorIndex * sectorStep) }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="flex"
            >
              {shapingCards.map((item, index) => (
                <div
                  key={`${item.title}-${index}`}
                  data-sector-card
                  className="mr-5 w-[86%] shrink-0 sm:w-[70%] md:w-[47%] xl:w-[31%]"
                >
                  <Link to={item.to} className="block">
                    <div className="overflow-hidden rounded-[1.4rem]">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-[185px] w-full object-cover transition duration-700 hover:scale-[1.04] sm:h-[205px] lg:h-[225px]"
                      />
                    </div>

                    <div className="pt-4">
                      <h3 className="text-[1.28rem] font-semibold leading-[1.08] tracking-[-0.03em] text-black sm:text-[1.45rem] lg:text-[1.3rem]">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-sm leading-7 text-black/70">
                        {item.text}
                      </p>
                    </div>
                  </Link>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="mt-10 max-w-[900px]">
            <p className="text-[1.02rem] leading-[1.42] tracking-[-0.015em] text-black sm:text-[1.14rem] lg:text-[1.3rem]">
              We combine future-focused thinking and technical know-how to
              power local impact. Our teams develop deep understanding and a
              partnership of trust with our clients. We inspire, guide,
              collaborate and deliver across water, monitoring and
              infrastructure systems.
            </p>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-y border-cyan-300/10 bg-[#07141d]">
        <motion.div
          aria-hidden="true"
          initial={{ scale: 1.06, y: 0 }}
          whileInView={{ scale: 1.02, y: -18 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.8, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img
            src={whatWeDoBackground}
            alt="Hydrotec infrastructure background"
            className="h-full w-full object-cover object-center"
          />
        </motion.div>

        <div className="absolute inset-0 bg-[#04111a]/52" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#031018]/72 via-[#062031]/54 to-[#0a3142]/44" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#031018]/72 via-transparent to-[#031018]/18" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.16),transparent_26%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.14),transparent_30%)]" />

        <div className="relative mx-auto max-w-[1600px] px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <div className="mb-14 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div className="max-w-4xl">
              <SectionEyebrow>What we do</SectionEyebrow>
              <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl lg:leading-[1.02]">
                Infrastructure systems across access, treatment, flow, and
                monitoring.
              </h2>
            </div>

            <p className="max-w-xl text-base leading-8 text-white/74 lg:justify-self-end">
              From public refill systems to monitoring-led treatment
              infrastructure, Hydrotec creates connected water systems that
              are practical, resilient, and deployment-ready.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {solutionCards.map((item, index) => (
              <FeatureCard key={item.title} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-b from-[#eef6f5] via-white to-[#f4f8f8] text-slate-900">
        <div className="mx-auto max-w-[1600px] px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="mb-10 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.34em] text-cyan-700/80">
                Featured Solutions
              </p>
              <h2 className="mt-3 max-w-3xl text-3xl font-semibold tracking-[-0.04em] text-slate-900 sm:text-4xl lg:text-5xl">
                
              </h2>
            </div>

            <p className="max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
              Explore selected Hydrotec capabilities across access,
              monitoring, treatment, and public-impact systems.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1.35fr_0.9fr] lg:items-stretch">
            <div className="relative min-h-[300px] overflow-hidden rounded-[2rem] bg-slate-200 shadow-[0_30px_80px_rgba(15,23,42,0.14)] sm:min-h-[380px] lg:min-h-[560px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={showcaseIndex}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  className="absolute inset-0"
                >
                  <img
                    src={currentShowcase.image}
                    alt={currentShowcase.title}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#02131b]/45 via-transparent to-[#0b7c89]/18" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </motion.div>
              </AnimatePresence>

              <div className="absolute left-5 top-5 z-10 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-md">
                <span className="text-xs font-medium uppercase tracking-[0.28em] text-white/90">
                  Featured Work
                </span>
              </div>

              <div className="absolute bottom-5 left-5 right-5 z-10 flex gap-2">
                {showcaseProjects.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setShowcaseIndex(i)}
                    className="group flex-1"
                    aria-label={`Go to project ${i + 1}`}
                  >
                    <span className="block h-[4px] overflow-hidden rounded-full bg-white/25">
                      <span
                        className={`block h-full rounded-full transition-all duration-500 ${
                          i === showcaseIndex ? "w-full bg-white" : "w-0 bg-white/70"
                        }`}
                      />
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col justify-between rounded-[2rem] border border-slate-200/80 bg-white/80 p-7 shadow-[0_20px_60px_rgba(15,23,42,0.06)] backdrop-blur-sm sm:p-8 lg:p-10">
              <div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`content-${showcaseIndex}`}
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                  >
                    <p className="text-xs font-semibold uppercase tracking-[0.32em] text-emerald-600">
                      {String(showcaseIndex + 1).padStart(2, "0")} /{" "}
                      {String(showcaseProjects.length).padStart(2, "0")}
                    </p>

                    <h3 className="mt-4 max-w-[500px] text-[2rem] font-semibold leading-[1] tracking-[-0.05em] text-slate-900 sm:text-[2.5rem] lg:text-[3rem]">
                      {currentShowcase.title}
                    </h3>

                    <p className="mt-6 max-w-[520px] text-base leading-8 text-slate-600 sm:text-[1.05rem]">
                      {currentShowcase.text}
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                      {showcaseIsExternal ? (
                        <a
                          href={currentShowcase.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center rounded-full bg-slate-900 px-7 py-3.5 text-sm font-semibold text-white transition hover:translate-y-[-1px] hover:bg-cyan-800"
                        >
                          {currentShowcase.cta}
                        </a>
                      ) : (
                        <Link
                          to={currentShowcase.to}
                          className="inline-flex items-center justify-center rounded-full bg-slate-900 px-7 py-3.5 text-sm font-semibold text-white transition hover:translate-y-[-1px] hover:bg-cyan-800"
                        >
                          {currentShowcase.cta}
                        </Link>
                      )}

                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center rounded-full border border-slate-300 px-7 py-3.5 text-sm font-semibold text-slate-700 transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-800"
                      >
                        Request consultation
                      </Link>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-12 flex flex-col gap-6 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handlePrevShowcase}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-800"
                  >
                    <ChevronRight className="h-5 w-5 rotate-180" />
                  </button>

                  <button
                    type="button"
                    onClick={handleNextShowcase}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-800"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  {showcaseProjects.map((project, i) => (
                    <button
                      key={project.title}
                      type="button"
                      onClick={() => setShowcaseIndex(i)}
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        i === showcaseIndex
                          ? "w-10 bg-cyan-700"
                          : "w-2.5 bg-slate-300 hover:bg-slate-400"
                      }`}
                      aria-label={`Show ${project.title}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {featuredSections.map((item, index) => (
        <EditorialSection
          key={item.title}
          {...item}
          reverse={index % 2 === 1}
        />
      ))}


      <section className="bg-white py-24 text-center">
        <p className="text-xs uppercase tracking-[0.8em] text-gray-900">
          Recognized by
        </p>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          className="mx-auto mt-12 flex max-w-[1400px] flex-wrap justify-center gap-10 sm:gap-12 lg:gap-16"
        >
          {recognitionLogos.map((item, i) => (
            <a
              key={i}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="transition duration-300 hover:scale-[1.02]"
            >
              <motion.img
                src={item.logo}
                variants={fadeUp}
                className="h-[72px] opacity-70 transition duration-300 hover:opacity-100 sm:h-[86px] lg:h-[100px]"
                alt={`Recognition logo ${i + 1}`}
              />
            </a>
          ))}
        </motion.div>
      </section>

      <HydrotecBrandSection />
    </div>
  );
}